import { model, Model } from 'mst-collection';
import { types, getParent, IAnyModelType } from 'mobx-state-tree';

export class SubCategory extends Model({
  id: types.number,
  name: types.string,
  description: types.string,
  videosCount: types.number,
  children: types.array(
    types.late((): IAnyModelType => SubCategoryModel),
  ),

  isSelected: false,
  isPreselected: false,
  isExpanded: false,
}) {
  private get _parentCategory(): Category {
    return getParent<Category>(this, 2);
  }

  get hasSelection() {
    return this.isPreselected;
  }

  toggleIsExpanded() {
    this.isExpanded = !this.isExpanded;
  }

  setIsPreselectedFromChildren(value: boolean) {
    if (!value) {
      this.isPreselected = false;
    } else {
      // When all subcategories (children) are selected this means that a whole category is selected
      if (
        this.children.length ===
        this.children.filter((i) => i.isPreselected).length
      ) {
        this.isPreselected = true;
      }
    }
  }

  setIsSelectedFromChildren(value: boolean) {
    if (!value) {
      this.isSelected = false;
    } else {
      // When all subcategories (children) are selected this means that a whole category is selected
      if (
        this.children.length ===
        this.children.filter((i) => i.isSelected).length
      ) {
        this.isSelected = true;
      }
    }
  }

  setIsPreselected(value: boolean) {
    this.isPreselected = value;
    this.children.forEach((i) => i.setIsPreselected(value));
    this._parentCategory.setIsPreselectedFromChildren(value);
  }

  setIsSelected(value: boolean) {
    this.isSelected = value;
    this.children.forEach((i) => i.setIsSelected(value));
    this._parentCategory.setIsSelectedFromChildren(value);
  }

  applyPreSelection() {
    this.isSelected = this.isPreselected;
    this.children.forEach((i) => i.applyPreSelection());
  }

  clearSelection() {
    this.isSelected = false;
    this.children.forEach((i) => i.clearSelection());
  }

  resetPreSelection() {
    this.isPreselected = this.isSelected;
    this.children.forEach((i) => i.resetPreSelection());
  }

  get canBeAppliedAsFilter() {
    return (
      this.isSelected ||
      this.children.some((i) => i.canBeAppliedAsFilter)
    );
  }

  get selectedIds() {
    // When category is selected it includes selection of all subcategories
    if (this.isSelected) {
      return this.id;
    }

    return this.children
      .filter((i) => i.canBeAppliedAsFilter)
      .map((i) => i.selectedIds)
      .flat();
  }

  get selectedNames() {
    // When category is selected it includes selection of all subcategories
    if (this.isSelected) {
      return this.name;
    }

    return this.children
      .filter((i) => i.canBeAppliedAsFilter)
      .map((i) => i.selectedNames)
      .flat();
  }
}

const SubCategoryModel = model(SubCategory);

export class Category extends Model({
  id: types.identifierNumber,
  name: types.string,
  description: types.string,
  videosCount: types.number,
  children: types.array(SubCategoryModel),

  // isSelected is using as a value for filters, when user press "apply filters"
  //  we set isPreselected value to isSelected and fetch data by new filters
  isSelected: false,
  // isPreselected is using as a "local" value on screens
  isPreselected: false,
  isExpanded: false,
}) {
  get hasSelection() {
    return this.isPreselected;
  }

  get hasSelectionInChildren() {
    return this.children.some((c) => c.hasSelection);
  }

  toggleIsExpanded() {
    this.isExpanded = !this.isExpanded;
  }

  setIsPreselectedFromChildren(value: boolean) {
    if (!value) {
      this.isPreselected = false;
    } else {
      // When all subcategories (children) are selected this means that a whole category is selected
      if (
        this.children.length ===
        this.children.filter((i) => i.isPreselected).length
      ) {
        this.isPreselected = true;
      }
    }
  }

  setIsSelectedFromChildren(value: boolean) {
    if (!value) {
      this.isSelected = false;
    } else {
      // When all subcategories (children) are selected this means that a whole category is selected
      if (
        this.children.length ===
        this.children.filter((i) => i.isSelected).length
      ) {
        this.isSelected = true;
      }
    }
  }

  setIsPreselected(value: boolean) {
    this.isPreselected = value;
    // When we enable or disable the category we also need to enable or disable  sub categories
    this.children.forEach((i) => i.setIsPreselected(value));
  }

  setIsSelected(value: boolean) {
    this.isSelected = value;
    // When we enable or disable the category we also need to enable or disable  sub categories
    this.children.forEach((i) => i.setIsSelected(value));
  }

  clearSelection() {
    this.isSelected = false;
    this.children.forEach((i) => i.clearSelection());
  }

  resetPreSelection() {
    this.isPreselected = this.isSelected;
    this.children.forEach((i) => i.resetPreSelection());
  }

  applyPreSelection() {
    this.isSelected = this.isPreselected;
    this.children.forEach((i) => i.applyPreSelection());
  }

  get canBeAppliedAsFilter() {
    return (
      this.isSelected ||
      this.children.some((i) => i.canBeAppliedAsFilter)
    );
  }

  get selectedIds() {
    // When category is selected it includes selection of all subcategories
    if (this.isSelected) {
      return this.id;
    }

    return this.children
      .filter((i) => i.canBeAppliedAsFilter)
      .map((i) => i.selectedIds)
      .flat();
  }

  get selectedNames() {
    // When category is selected it includes selection of all subcategories
    if (this.isSelected) {
      return this.name;
    }

    return this.children
      .filter((i) => i.canBeAppliedAsFilter)
      .map((i) => i.selectedNames)
      .flat();
  }
}

export const CategoryModel = model(Category);
